import { useEffect, useState } from 'react';
import { MagnifyingGlassIcon, UsersIcon } from '@heroicons/react/24/outline';
import axios from '../../../utils/axios';

type UserTab = 'all' | 'user' | 'admin' | 'super-admin';

const ROLE_BADGE: Record<string, string> = {
  user:         'bg-green-900/60 text-green-400 border-green-500/20',
  admin:        'bg-amber-900/60 text-amber-400 border-amber-500/20',
  'super-admin':'bg-sky-900/60 text-sky-400 border-sky-500/20',
};

const thCls = 'text-left text-white/30 text-[9px] font-semibold uppercase tracking-wider py-3 pr-5 whitespace-nowrap';
const tdCls = 'py-3 pr-5 border-t border-white/5 text-white/70 text-xs align-middle';

export default function UserManagementView() {
  const [users, setUsers]   = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]       = useState<UserTab>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    axios
      .get('/users/all')
      .then((res) => { if (res.status === 200) setUsers(res.data?.data ?? res.data ?? []); })
      .catch(() => { /* no data */ })
      .finally(() => setLoading(false));
  }, []);

  const normalise = (role?: string) => {
    const r = role?.toLowerCase();
    if (r === 'admin') return 'admin';
    if (r === 'user')  return 'user';
    if (r)             return 'super-admin';
    return 'user';
  };

  const filtered = users.filter((u: any) => {
    const q = search.toLowerCase();
    const matchSearch = !q || u.name?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q);
    const matchTab = tab === 'all' || normalise(u.role) === tab;
    return matchSearch && matchTab;
  });

  const counts = {
    all:          users.length,
    user:         users.filter((u: any) => normalise(u.role) === 'user').length,
    admin:        users.filter((u: any) => normalise(u.role) === 'admin').length,
    'super-admin':users.filter((u: any) => normalise(u.role) === 'super-admin').length,
  };

  const tabs: { id: UserTab; label: string }[] = [
    { id: 'all',         label: `All (${counts.all})` },
    { id: 'user',        label: `Users (${counts.user})` },
    { id: 'admin',       label: `Admins (${counts.admin})` },
    { id: 'super-admin', label: `Super Admins (${counts['super-admin']})` },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
            User Management
          </h2>
          <p className="text-white/30 text-xs mt-0.5">{users.length} registered accounts</p>
        </div>
        <div className="relative">
          <MagnifyingGlassIcon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
          <input
            className="bg-white/5 border border-white/10 text-white text-xs rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20 w-52"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-white/6">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-xs font-medium rounded-t-lg transition-all border-b-2 -mb-px ${
              tab === t.id
                ? 'text-green-400 border-green-500'
                : 'text-white/35 border-transparent hover:text-white/60'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-6 h-6 rounded-full border-2 border-green-500/30 border-t-green-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <UsersIcon className="w-12 h-12 text-white/10" />
          <p className="text-white/25 text-sm">No users found</p>
        </div>
      ) : (
        <div className="rounded-xl border border-white/6 overflow-hidden" style={{ background: '#0a2e18' }}>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.15)' }}>
                <th className={`${thCls} pl-5`}>#</th>
                <th className={thCls}>Name</th>
                <th className={`${thCls} hidden sm:table-cell`}>Email</th>
                <th className={thCls}>Role</th>
                <th className={`${thCls} hidden md:table-cell`}>Joined</th>
                <th className={`${thCls} pr-5 text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u: any, idx: number) => {
                const role = normalise(u.role);
                return (
                  <tr key={idx} className="hover:bg-white/3 transition-colors">
                    <td className={`${tdCls} pl-5 text-white/25 tabular-nums`}>{idx + 1}</td>
                    <td className={tdCls}>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-green-800 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                          {(u.name || u.username || '?')[0].toUpperCase()}
                        </div>
                        <span className="font-semibold text-white/80">{u.name || '—'}</span>
                      </div>
                    </td>
                    <td className={`${tdCls} text-white/40 hidden sm:table-cell`}>{u.username || '—'}</td>
                    <td className={tdCls}>
                      <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold capitalize ${ROLE_BADGE[role] || ROLE_BADGE['user']}`}>
                        {role}
                      </span>
                    </td>
                    <td className={`${tdCls} text-white/35 hidden md:table-cell`}>
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td className={`${tdCls} pr-5 text-right`}>
                      <div className="flex items-center justify-end gap-2">
                        <select
                          defaultValue={u.role || 'user'}
                          className="bg-[#0a2e18] border border-white/10 text-white/70 text-[10px] rounded px-2 py-1 focus:outline-none appearance-none cursor-pointer"
                          title="Change role"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          <option value="super-admin">Super Admin</option>
                        </select>
                        <button className="text-sky-400 text-[10px] hover:text-sky-300 transition-colors">Save</button>
                        <span className="text-white/15">|</span>
                        <button className="text-red-400 text-[10px] hover:text-red-300 transition-colors">Deactivate</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
